import { Flex } from '@chakra-ui/react';
import { Container, Group } from '@melio/penny';
import { type Plan, SubscriptionBillingCycleEnum } from '@melio/subscriptions-api';
import { useSubscription } from '@melio/subscriptions-sdk';
import { useState } from 'react';

import { PlanCard } from '../components/PlanCard';
import { PlansLoader } from '../components/PlansLoader';
import { PlansSelectionHeader } from '../components/PlansSelectionHeader';
import { SubscribeToPlanType } from '../types';

export type SubscriptionPlansScreenProps = {
  plans: Plan[];
  isLoading?: boolean;
  freePlanId?: string;
  isSavingFreePlan?: boolean;
  onSelectPlan: ({ planId, price }: SubscribeToPlanType) => void;
};
export const SubscriptionPlansScreen = ({
  plans,
  isLoading,
  freePlanId,
  isSavingFreePlan,
  onSelectPlan,
  ...rest
}: SubscriptionPlansScreenProps) => {
  const { subscription } = useSubscription();
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(
    subscription?.billingCycle ?? SubscriptionBillingCycleEnum.Monthly
  );

  const changeBillingCycle = () => {
    if (selectedBillingCycle === SubscriptionBillingCycleEnum.Monthly) {
      setSelectedBillingCycle(SubscriptionBillingCycleEnum.Yearly);
      return;
    }
    setSelectedBillingCycle(SubscriptionBillingCycleEnum.Monthly);
  };
  return (
    <Container paddingX="xxs" paddingY="xxs" data-testid="subscription-plans-screen">
      <Group spacing="none" justifyContent="center" variant="vertical" {...rest}>
        <PlansSelectionHeader selectedBillingCycle={selectedBillingCycle} onCycleChange={changeBillingCycle} />

        {isLoading ? (
          <PlansLoader />
        ) : (
          <Flex gap="m" pt="xxl" pb="xxxs" flexWrap="wrap">
            {plans.map((plan) => {
              const isCurrentPlan = plan.id === subscription.planId;
              const currentBillingCycle = subscription.billingCycle;
              const isSwitchToYearly =
                selectedBillingCycle === SubscriptionBillingCycleEnum.Yearly &&
                currentBillingCycle === SubscriptionBillingCycleEnum.Monthly;
              const isCurrentDisabled = isCurrentPlan && (!isSwitchToYearly || plan.id === freePlanId);

              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selectedBillingCycle={selectedBillingCycle}
                  onSelectPlan={onSelectPlan}
                  isFreePlan={plan.id === freePlanId}
                  isSaving={isSavingFreePlan && freePlanId === plan.id}
                  isCurrentPlan={isCurrentPlan}
                  isSwitchingBillingCycle={isSwitchToYearly}
                  isDisabled={isSavingFreePlan || isCurrentDisabled}
                />
              );
            })}
          </Flex>
        )}
      </Group>
    </Container>
  );
};
